import React, { useEffect, useState } from 'react';
import { Mail, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from './DashboardLayout';

const STATUS_COLORS = { sent: { bg: '#d1fae5', color: '#065f46' }, failed: { bg: '#fee2e2', color: '#991b1b' } };
const STATUS_LABELS = { sent: 'Gesendet', failed: 'Fehlgeschlagen' };
const TYPE_LABELS = { all_users: 'Alle Anbieter', certified: 'Zertifizierte', selected: 'Ausgewählte' };

function MailDetailModal({ log, onClose }) {
  if (!log) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, maxWidth: 700, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 24 }} onClick={e => e.stopPropagation()} data-testid="mail-detail-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>E-Mail-Details</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>&times;</button>
        </div>
        <table style={{ width: '100%', fontSize: 13 }}>
          <tbody>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0', verticalAlign: 'top', width: 120 }}>Betreff:</td><td style={{ padding: '6px 0' }}>{log.subject}</td></tr>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0' }}>Empfänger:</td><td style={{ padding: '6px 0' }}>{log.recipient_name} &lt;{log.recipient_email}&gt;</td></tr>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0' }}>Gesendet von:</td><td style={{ padding: '6px 0' }}>{log.sent_by_email}</td></tr>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0' }}>Datum:</td><td style={{ padding: '6px 0' }}>{new Date(log.sent_at).toLocaleString('de-DE')}</td></tr>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0' }}>Typ:</td><td style={{ padding: '6px 0' }}>{TYPE_LABELS[log.recipient_type] || log.recipient_type}</td></tr>
            <tr><td style={{ fontWeight: 600, padding: '6px 12px 6px 0' }}>Vorlage:</td><td style={{ padding: '6px 0' }}>{log.template_key || '(Freier Text)'}</td></tr>
          </tbody>
        </table>
        <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Inhalt:</div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: log.body_html }} />
        </div>
      </div>
    </div>
  );
}

export default function MailLogPage({ providerId }) {
  const { apiFetch } = useApi();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const limit = 20;

  useEffect(() => { loadLogs(); }, [page, providerId]);

  const loadLogs = async () => {
    let url = `/api/mail/log?limit=${limit}&skip=${page * limit}`;
    if (providerId) url += `&provider_id=${providerId}`;
    const r = await apiFetch(url);
    if (!r) return;
    try { const d = await r.json(); setLogs(d.logs || []); setTotal(d.total || 0); } catch {}
  };

  const totalPages = Math.ceil(total / limit);
  const isEmbedded = !!providerId;

  return (
    <div data-testid="mail-log-page">
      {!isEmbedded && (
        <div className="page-header">
          <h2>E-Mail-Protokoll</h2>
          <p>{total} E-Mails gesendet</p>
        </div>
      )}

      {logs.length === 0 ? (
        <div className={isEmbedded ? '' : 'card'} style={{ padding: isEmbedded ? '12px 0' : 32, textAlign: 'center' }}>
          <Mail size={24} style={{ color: '#d1d5db', margin: '0 auto 8px' }} />
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Noch keine E-Mails gesendet</p>
        </div>
      ) : (
        <>
          <div className={isEmbedded ? '' : 'card'}>
            <div className="table-wrap">
              <table data-testid="mail-log-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Betreff</th>
                    {!isEmbedded && <th>Empfänger</th>}
                    <th>Typ</th>
                    <th style={{ width: 80 }}>Status</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} data-testid={`mail-log-row-${i}`}>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(log.sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{log.subject?.substring(0, 60)}{log.subject?.length > 60 ? '...' : ''}</td>
                      {!isEmbedded && <td style={{ fontSize: 12 }}>{log.recipient_name || log.recipient_email}</td>}
                      <td><span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280' }}>{TYPE_LABELS[log.recipient_type] || log.recipient_type}</span></td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12, ...(STATUS_COLORS[log.status] || {}) }}>
                          {STATUS_LABELS[log.status] || log.status}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => setDetail(log)} title="Details" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} data-testid={`mail-log-detail-${i}`}>
                          <Eye size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: page === 0 ? 'default' : 'pointer' }}>
                <ChevronLeft size={16} />
              </button>
              <span style={{ fontSize: 13 }}>Seite {page + 1} von {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: page >= totalPages - 1 ? 'default' : 'pointer' }}>
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}

      <MailDetailModal log={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
